import { Channel } from '@at/sauron-platform-core'
import { parse } from 'query-string'
import { SearchFilters302 } from '@at/track-types'

const getFirstValue = (queryParamValue: (string | null)[] | string[] | string | null) => {
    const value = Array.isArray(queryParamValue) ? queryParamValue[0] : queryParamValue
    return value ?? undefined
}
const getValueAsArray = (queryParamValue: (string | null)[] | string[] | string | null) => {
    if (Array.isArray(queryParamValue)) {
        const value = queryParamValue.filter((x) => x !== null) as string[]
        return value.length > 0 ? value : undefined
    } else {
        return !!queryParamValue ? [queryParamValue] : undefined
    }
}

export const getFirstValueAsNumber = (queryParamValue: (string | null)[] | string[] | string | null) => {
    const value = Array.isArray(queryParamValue) ? queryParamValue[0] : queryParamValue
    if (!!value) {
        return parseFloat(value)
    }
    return undefined
}

const getValueAsNumberArray = (queryParamValue: (string | null)[] | string[] | string | null) => {
    if (Array.isArray(queryParamValue)) {
        const value = queryParamValue.filter((x) => x !== null) as string[]
        return value.length > 0 ? value.map((x) => parseInt(x)) : undefined
    } else {
        return !!queryParamValue ? [parseInt(queryParamValue)] : undefined
    }
}

export const getLatLongAsNumbers = (queryParamValue: (string | null)[] | string[] | string | null) => {
    const roundToTwoDecimalPlaces = (num: string) => {
        if (isNaN(parseFloat(num))) {
            return undefined
        } else {
            return parseFloat(parseFloat(num).toFixed(2))
        }
    }
    if (!Array.isArray(queryParamValue)) {
        const [latitude, longitude] = queryParamValue?.split(', ') ?? []
        return {
            latitude: roundToTwoDecimalPlaces(latitude),
            longitude: roundToTwoDecimalPlaces(longitude),
        }
    }
}

export const getSearchFiltersContextData = (searchUrl: string, id = 'currentSearch'): SearchFilters302 => {
    const result = parse(searchUrl)

    const latLong = getLatLongAsNumbers(result['lat-long'])
    const postcode = getFirstValue(result.postcode)

    const acceleration_range = getValueAsArray(result['zero-to-60'])
    const annual_mileage = getFirstValueAsNumber(result['yearly-mileage'])
    const annual_tax = getFirstValue(result['annual-tax-cars'])
    const axle_config = getFirstValue(result['axle-config'])
    const battery_quick_time = getFirstValue(result['battery-quick-time'])
    const battery_range = getFirstValue(result['battery-range'])
    const battery_time = getFirstValue(result['battery-time'])
    const bedroom_layout = getFirstValue(result['bedroom-layout'])
    const berth = getValueAsArray(result['berth'])
    const body_type = getValueAsArray(result['body-type'])
    const boot_size_values = getValueAsArray(result['bootSizeValues'])
    const cab_type = getValueAsArray(result['cab-type'])
    const category_tag = getValueAsArray(result['category-tag'])
    const click_and_collect_available = getFirstValue(result['click-and-collect-available']) === 'on' ? true : undefined
    const co2_emissions = getFirstValue(result['co2-emissions-cars'])
    const colour = getValueAsArray(result['colour'])
    const deposit = getFirstValueAsNumber(result['deposit'])
    const driver_position = getFirstValue(result['driver-position'])
    const drivetrain = getValueAsArray(result['drivetrain'])
    const emission_class = getValueAsArray(result['emission-class'])
    const end_layout = getFirstValue(result['end-layout'])
    const engine_power_from = getFirstValueAsNumber(result['min-engine-power'])
    const engine_power_to = getFirstValueAsNumber(result['max-engine-power'])
    const engine_size_from = getFirstValueAsNumber(result['minimum-badge-engine-size'])
    const engine_size_to = getFirstValueAsNumber(result['maximum-badge-engine-size'])
    const exclude_cat_scdn = getFirstValue(result['exclude-writeoff-categories']) === 'on' ? true : undefined
    const fuel_consumption = getFirstValue(result['fuel-consumption'])
    const fuel_type = getValueAsArray(result['fuel-type'])
    const gearbox = getValueAsArray(result['transmission'])
    const home_delivery_available = getFirstValue(result['only-delivery-option']) === 'on' ? true : undefined
    const insurance_group = getFirstValue(result['insuranceGroup'])
    const keywords = getFirstValue(result['keywords'])
    const lease_product_type = getFirstValue(result['lease-product-type'])
    const make = getValueAsArray(result.make)
    const manufactured_year_from = getFirstValue(result['year-from'])
    const manufactured_year_to = getFirstValue(result['year-to'])
    const max_engine_size_cc = getFirstValueAsNumber(result['cc-to'])
    const max_exterior_length = getFirstValueAsNumber(result['max-exterior-length'])
    const max_gross_vehicle_weight = getFirstValueAsNumber(result['max-gross-vehicle-weight'])
    const max_hours_used = getFirstValueAsNumber(result['max-hours-used'])
    const max_lease_monthly_price = getFirstValueAsNumber(result['max-lease-monthly-price'])
    const max_lease_monthly_price_ex_vat = getFirstValueAsNumber(result['max-lease-monthly-price-ex-vat'])
    const max_mtplm = getFirstValueAsNumber(result['max-mtplm'])
    const mileage_from = getFirstValueAsNumber(result['minimum-mileage'])
    const mileage_to = getFirstValueAsNumber(result['maximum-mileage'])
    const min_engine_size_cc = getFirstValueAsNumber(result['cc-from'])
    const min_exterior_length = getFirstValueAsNumber(result['min-exterior-length'])
    const min_gross_vehicle_weight = getFirstValueAsNumber(result['min-gross-vehicle-weight'])
    const min_hours_used = getFirstValueAsNumber(result['min-hours-used'])
    const min_lease_monthly_price = getFirstValueAsNumber(result['min-lease-monthly-price'])
    const min_lease_monthly_price_ex_vat = getFirstValueAsNumber(result['min-lease-monthly-price-ex-vat'])
    const min_mtplm = getFirstValueAsNumber(result['min-mtplm'])
    const model = getValueAsArray(result.model)
    const model_variant = getValueAsArray(result['aggregatedTrim'])
    const monthly_price_from = getFirstValueAsNumber(result['min-monthly-price'])
    const monthly_price_to = getFirstValueAsNumber(result['max-monthly-price'])
    const no_of_doors = getValueAsNumberArray(result['quantity-of-doors'])
    const only_show_cat_scdn = getFirstValue(result['only-writeoff-categories']) === 'on' ? true : undefined
    const only_show_manufacturer_approved = getFirstValue(result['ma']) === 'Y' ? true : undefined
    const only_show_new_car_deals = getFirstValue(result['newCarHasDeal']) === 'on' ? true : undefined
    const only_show_northern_ireland_adverts = getFirstValue(result['ni-only']) === 'on' ? true : undefined
    const part_exchange_available =
        getFirstValue(result['part-ex-available']) === 'true' || getFirstValue(result['part-ex-available']) === 'on'
            ? true
            : undefined
    const search_radius = getFirstValueAsNumber(result.radius)
    const seats_from = getFirstValueAsNumber(result['minimum-seats'])
    const seats_to = getFirstValueAsNumber(result['maximum-seats'])
    const seats_values = getValueAsNumberArray(result['seats_values'])
    const seller_type = getValueAsArray(result['seller-type'])
    const show_additional_adverts = getFirstValue(result['include-non-classified']) === 'on' ? true : undefined
    const sort_by = getFirstValue(result['sort'])
    const style = getFirstValue(result['style'])
    const sub_style = getFirstValue(result['sub-style'])
    const supplied_price_from = getFirstValueAsNumber(result['supplied-price-from'])
    const supplied_price_to = getFirstValueAsNumber(result['supplied-price-to'])
    const term = getFirstValueAsNumber(result['term'])
    const total_price_from = getFirstValueAsNumber(result['price-from'])
    const total_price_to = getFirstValueAsNumber(result['price-to'])
    const trailer_axle_number = getFirstValue(result['trailer_axle_number'])
    const vat_status = getFirstValue(result['vat-status'])
    const wheelbase = getValueAsArray(result['wheelbase'])

    return {
        SCHEMA_KEY: 'SearchFilters302',
        id: id,
        acceleration_range_values: acceleration_range,
        annual_mileage: annual_mileage,
        annual_tax: annual_tax,
        axle_config: axle_config,
        battery_charge_time: battery_time,
        battery_quick_charge_time: battery_quick_time,
        battery_range: battery_range,
        bedroom_layout: bedroom_layout,
        berth: berth,
        body_type: body_type,
        boot_space_values: boot_size_values,
        cab_type: cab_type,
        category_tag: category_tag,
        click_and_collect_available: click_and_collect_available,
        co2_emissions: co2_emissions,
        colour: colour,
        deposit: deposit,
        driver_position: driver_position,
        drivetrain: drivetrain,
        emission_class: emission_class,
        end_layout: end_layout,
        engine_power_from: engine_power_from,
        engine_power_to: engine_power_to,
        engine_size_from: engine_size_from,
        engine_size_to: engine_size_to,
        exclude_cat_scdn: exclude_cat_scdn,
        fuel_consumption: fuel_consumption,
        fuel_type: fuel_type,
        gearbox: gearbox,
        home_delivery_available: home_delivery_available,
        insurance_group: insurance_group,
        keywords: keywords,
        latitude: latLong?.latitude,
        lease_product_type: lease_product_type,
        longitude: latLong?.longitude,
        make: make,
        manufactured_year_from: manufactured_year_from,
        manufactured_year_to: manufactured_year_to,
        max_engine_size_cc: max_engine_size_cc,
        max_exterior_length: max_exterior_length,
        max_gross_vehicle_weight: max_gross_vehicle_weight,
        max_hours_used: max_hours_used,
        max_lease_monthly_price: max_lease_monthly_price,
        max_lease_monthly_price_ex_vat: max_lease_monthly_price_ex_vat,
        max_mtplm: max_mtplm,
        mileage_from: mileage_from,
        mileage_to: mileage_to,
        min_engine_size_cc: min_engine_size_cc,
        min_exterior_length: min_exterior_length,
        min_gross_vehicle_weight: min_gross_vehicle_weight,
        min_hours_used: min_hours_used,
        min_lease_monthly_price: min_lease_monthly_price,
        min_lease_monthly_price_ex_vat: min_lease_monthly_price_ex_vat,
        min_mtplm: min_mtplm,
        model: model,
        model_variant: model_variant,
        monthly_price_from: monthly_price_from,
        monthly_price_to: monthly_price_to,
        no_of_doors: no_of_doors,
        no_of_seats: seats_values,
        only_show_cat_scdn: only_show_cat_scdn,
        only_show_manufacturer_approved: only_show_manufacturer_approved,
        only_show_new_car_deals: only_show_new_car_deals,
        only_show_northern_ireland_adverts: only_show_northern_ireland_adverts,
        part_exchange_available: part_exchange_available,
        postcode: postcode,
        search_radius: search_radius,
        seats_from: seats_from,
        seats_to: seats_to,
        seller_type: seller_type,
        show_additional_adverts: show_additional_adverts,
        sort_by: sort_by,
        style: style,
        sub_style: sub_style,
        supplied_price_from: supplied_price_from,
        supplied_price_to: supplied_price_to,
        term: term,
        total_price_from: total_price_from,
        total_price_to: total_price_to,
        trailer_axle_number: trailer_axle_number,
        vat_status: vat_status,
        wheelbase: wheelbase,
    }
}

export const mapSearchData: (search: string) => SearchFilters302 = (search) => getSearchFiltersContextData(search)

export const pageName: (channel: Channel, isRetailerStockSearch: boolean, isLeasingSearch: boolean) => string = (
    channel,
    isRetailerStockSearch,
    isLeasingSearch
) => {
    if (isRetailerStockSearch) {
        return `${channel}:search:dealers:profile:stock`
    }
    if (isLeasingSearch) {
        return `${channel}:leasing:search`
    }
    return `${channel}:search:known:results`
}
